import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { alpha } from '@mui/material/styles';
import { HelmetProvider } from 'react-helmet-async';

import App from './app';
import { mainRouter } from './routes/sections';
import { SENTRY_DSN, ENVIRONMENT } from './config-global';
import { grey, error, common, primary, success } from './theme/palette';

if (
  SENTRY_DSN &&
  (ENVIRONMENT === 'production' ||
    ENVIRONMENT === 'staging' ||
    ENVIRONMENT === 'staging-saas' ||
    ENVIRONMENT === 'production-saas' ||
    ENVIRONMENT === 'demo01' ||
    ENVIRONMENT === 'release')
) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    integrations: [
      ...(ENVIRONMENT === 'production' || ENVIRONMENT === 'production-saas'
        ? [Sentry.replayIntegration()]
        : []),
      Sentry.tanstackRouterBrowserTracingIntegration(mainRouter),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [
          [400, 499],
          [500, 599],
        ],
      }),
      Sentry.feedbackIntegration({
        // The floating actor is replaced by a trigger rendered inside the
        // nav sidebar (see layouts/common/nav-feedback-button.tsx), so it
        // can't overlap page content like confirmation buttons.
        autoInject: false,
        // Style the sentry feedback dialog to match our theme
        colorScheme: 'light',
        themeLight: {
          foreground: common.black,
          background: common.white,
          accentBackground: primary.main,
          accentForeground: primary.contrastText,
          successColor: success.main,
          errorColor: error.main,
          border: `1.5px solid ${alpha(grey[500], 0.24)}`,
          boxShadow: `0px 4px 24px 0px ${alpha(grey[500], 0.24)}`,
        },
        tags: {
          feedback: 'feedback',
        },
        showBranding: false,
        isEmailRequired: false,
        isNameRequired: false,
        formTitle: 'Have Feedback?',
        messagePlaceholder:
          "If you've got an idea, or something isn't working as you'd expect, please let us know.",
        submitButtonLabel: 'Send Feedback',
      }),
    ],
    // Session Replay (only in production)
    ...(ENVIRONMENT === 'production' || ENVIRONMENT === 'production-saas'
      ? {
          replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
          replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        }
      : {}),
  });

  // Let the Sentry feedback widget receive focus when a MUI Dialog is open.
  // MUI's FocusTrap listens for focusin at the document level and yanks focus
  // back into the dialog. We intercept in the capture phase and silence the
  // event when the target is inside #sentry-feedback, so MUI never sees it.
  // This is preferred over setting `disableEnforceFocus` on MuiDialog because
  // that would let Tab escape the dialog for keyboard users — this approach
  // exempts only the Sentry widget from the trap, so Tab cycling inside
  // dialogs still works for keyboard/screen-reader users.
  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target as HTMLElement | null;
      const sentryHost = document.getElementById('sentry-feedback');
      if (sentryHost && target && (sentryHost === target || sentryHost.contains(target))) {
        event.stopImmediatePropagation();
      }
    },
    true
  );
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
